/**
 * 이미지 관련 유틸리티 함수들
 */

export interface ImageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

export interface InstagramAspectRatioCheck {
  isValid: boolean;
  ratio: string;
  recommendation: string;
  ratioType: 'square' | 'portrait' | 'landscape' | 'invalid';
}

/**
 * 이미지 URL로부터 실제 이미지 크기를 가져옵니다.
 * @param imageUrl 이미지 URL
 * @returns Promise<ImageDimensions | null>
 */
export const getImageDimensions = (imageUrl: string): Promise<ImageDimensions | null> => {
  return new Promise((resolve) => {
    console.log('🖼️ 이미지 크기 측정 시작:', imageUrl.substring(0, 100) + '...');
    
    const img = new Image();
    
    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      console.log('✅ 이미지 로드 성공:', {
        url: imageUrl.substring(0, 100) + '...',
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: aspectRatio.toFixed(2)
      });
      
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: aspectRatio
      });
    };
    
    img.onerror = (error) => {
      console.error('❌ 이미지 로드 실패:', {
        url: imageUrl.substring(0, 100) + '...',
        error: error
      });
      resolve(null);
    };
    
    // CORS 문제 방지 시도 - S3의 경우 다른 설정 필요할 수 있음
    try {
      // S3 URL인 경우 crossOrigin 설정 없이 시도
      if (imageUrl.includes('amazonaws.com') || imageUrl.includes('s3.')) {
        console.log('🌐 S3 이미지 감지 - CORS 설정 없이 로드 시도');
        img.src = imageUrl;
      } else {
        // 다른 도메인의 이미지인 경우 crossOrigin 설정
        img.crossOrigin = 'anonymous';
        img.src = imageUrl;
      }
    } catch (corsError) {
      console.warn('⚠️ CORS 설정 오류, 기본 로드 시도:', corsError);
      img.src = imageUrl;
    }
    
    // 타임아웃 설정 (10초)
    setTimeout(() => {
      console.warn('⏰ 이미지 로드 타임아웃:', imageUrl.substring(0, 100) + '...');
      resolve(null);
    }, 10000);
  });
};

/**
 * Instagram 지원 화면 비율인지 확인합니다.
 * @param width 이미지 너비
 * @param height 이미지 높이
 * @returns InstagramAspectRatioCheck
 */
export const checkInstagramAspectRatio = (
  width: number, 
  height: number
): InstagramAspectRatioCheck => {
  const aspectRatio = width / height;
  
  // Instagram 지원 비율 (허용 오차 포함)
  const validRatios = [
    { 
      ratio: 1.0, 
      name: '정사각형 (1:1)', 
      type: 'square' as const,
      min: 0.95, 
      max: 1.05 
    },
    { 
      ratio: 0.8, 
      name: '세로 (4:5)', 
      type: 'portrait' as const,
      min: 0.75, 
      max: 0.85 
    },
    { 
      ratio: 1.91, 
      name: '가로 (1.91:1)', 
      type: 'landscape' as const,
      min: 1.85, 
      max: 1.95 
    }
  ];
  
  // 유효한 비율인지 확인
  for (const valid of validRatios) {
    if (aspectRatio >= valid.min && aspectRatio <= valid.max) {
      return {
        isValid: true,
        ratio: `${aspectRatio.toFixed(2)}:1 (${valid.name})`,
        recommendation: '✅ Instagram 포스팅 가능한 비율입니다.',
        ratioType: valid.type
      };
    }
  }
  
  // 가장 가까운 비율 찾기
  let closestRatio = validRatios[0];
  let minDiff = Math.abs(aspectRatio - closestRatio.ratio);
  
  for (const valid of validRatios) {
    const diff = Math.abs(aspectRatio - valid.ratio);
    if (diff < minDiff) {
      minDiff = diff;
      closestRatio = valid;
    }
  }
  
  return {
    isValid: false,
    ratio: `${aspectRatio.toFixed(2)}:1`,
    recommendation: `❌ Instagram 지원하지 않는 비율입니다. ${closestRatio.name}로 조정하세요.`,
    ratioType: 'invalid'
  };
};

/**
 * 이미지 URL의 Instagram 호환성을 체크합니다.
 * @param imageUrl 이미지 URL
 * @returns Promise<InstagramAspectRatioCheck | null>
 */
export const checkImageInstagramCompatibility = async (
  imageUrl: string
): Promise<InstagramAspectRatioCheck | null> => {
  try {
    const dimensions = await getImageDimensions(imageUrl);
    if (!dimensions) {
      return null;
    }
    
    return checkInstagramAspectRatio(dimensions.width, dimensions.height);
  } catch (error) {
    console.error('이미지 호환성 체크 실패:', error);
    return null;
  }
};

/**
 * 여러 이미지의 Instagram 호환성을 일괄 체크합니다.
 * @param imageUrls 이미지 URL 배열
 * @returns Promise<Map<string, InstagramAspectRatioCheck>>
 */
export const checkMultipleImagesCompatibility = async (
  imageUrls: string[]
): Promise<Map<string, InstagramAspectRatioCheck>> => {
  const results = new Map<string, InstagramAspectRatioCheck>();
  
  const checks = imageUrls.map(async (url) => {
    const result = await checkImageInstagramCompatibility(url);
    if (result) {
      results.set(url, result);
    }
    return { url, result };
  });
  
  await Promise.all(checks);
  return results;
};
